/**
 * Average draft position, live.
 *
 * The model export already carries an ADP, but it is regenerated by a full
 * model rebuild. ADP is the fastest-moving number on the board — a Saturday
 * injury moves it before it moves a projection or a consensus rank — so it
 * gets its own file and its own cheap fetch (`npm run fetch-adp`), and the
 * export's copy stays as the fallback when that fetch fails.
 *
 * Written by `model/scripts/fetch_adp.py` from Fantasy Football Calculator,
 * which aggregates real drafts and publishes the scatter around each pick.
 * That scatter is what makes the board's survival estimate honest rather than
 * a guess off our own ranking.
 */

import { indexPlayers } from './playerMatch.js';

const ADP_JSON = '/adp.json';

export const fetchAdp = async () => {
  const response = await fetch(ADP_JSON);
  if (!response.ok) {
    throw new Error(`Could not load ADP (${response.status}).`);
  }
  const payload = await response.json();
  if (!Array.isArray(payload?.players) || !payload.players.length) {
    throw new Error('ADP was empty.');
  }
  return payload;
};

let adpCache = null;

export const getAdpCached = async () => {
  if (adpCache) return adpCache;
  adpCache = await fetchAdp();
  return adpCache;
};

/**
 * Fold the live board onto ours. The feed wins; the export's ADP is the
 * fallback, so a failed fetch costs freshness and never the column.
 *
 * A row the feed does not carry is not "ADP 999" — it is a player deep enough
 * that nobody in the sample drafted him, and the honest column there is empty.
 */
export const mergeAdp = (board, payload) => {
  const lookup = indexPlayers(payload?.players || []);

  let matched = 0;
  const merged = board.map((row) => {
    const hit = lookup(row);
    if (!hit) return row;
    matched += 1;
    return {
      ...row,
      adp: hit.adp ?? row.adp ?? null,
      adpStdev: hit.adpStdev ?? row.adpStdev ?? null,
      adpTimesDrafted: hit.timesDrafted ?? null,
      bye: row.bye ?? hit.bye ?? null,
    };
  });

  return { board: merged, matched, fetchedAt: payload?.fetchedAt ?? null };
};

/** "3.05" — the round and pick an ADP lands on in a league of this size. */
export const formatAdpSlot = (adp, teams = 12) => {
  if (adp == null || !teams) return null;
  const pick = Math.max(1, Math.round(adp));
  const round = Math.ceil(pick / teams);
  const inRound = pick - (round - 1) * teams;
  return `${round}.${String(inRound).padStart(2, '0')}`;
};
